import { useLocation, useNavigate, Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { ArrowLeft, FileText, CheckCircle, XCircle, Loader2, Clock } from "lucide-react";
import { Spinner, SpinnerSize } from "@fluentui/react";
import type { CombinedResult, ApplicationResponse, DocStatus } from "../types/resultTypes";
import apiClient from "../services/apiClient";
import { API_ENDPOINTS } from "../config/api";
import { useTranslation } from "react-i18next";


const ScoreIndicator = ({
  value,
  max,
}: {
  value: number;
  max: number;
}) => {
  const { t } = useTranslation();
  const percent = Math.min((value / max) * 100, 100);
  const level: "low" | "mid" | "high" = percent > 75 ? "high" : percent >= 51 ? "mid" : "low";

  const config = {
    high: {
      label: t("resultPage.score.excellent"),
      scoreColor: "text-emerald-600",
      dotColor: "bg-emerald-500",
      badgeBg: "bg-emerald-50",
      badgeText: "text-emerald-700",
      badgeBorder: "border-emerald-200",
      segments: ["bg-emerald-500", "bg-emerald-500", "bg-emerald-500"],
    },
    mid: {
      label: t("resultPage.score.moderate"),
      scoreColor: "text-amber-500",
      dotColor: "bg-amber-400",
      badgeBg: "bg-amber-50",
      badgeText: "text-amber-700",
      badgeBorder: "border-amber-200",
      segments: ["bg-red-300", "bg-amber-400", "bg-gray-200"],
    },
    low: {
      label: t("resultPage.score.low"),
      scoreColor: "text-rose-600",
      dotColor: "bg-rose-500",
      badgeBg: "bg-rose-50",
      badgeText: "text-rose-700",
      badgeBorder: "border-rose-200",
      segments: ["bg-rose-500", "bg-gray-200", "bg-gray-200"],
    },
  }[level];

  return (
    <div className="flex flex-col items-center gap-2 w-full">
      <span className={`text-2xl font-extrabold tracking-tight ${config.scoreColor}`}>
        {percent.toFixed(0)}
        <span className="text-sm font-semibold ml-0.5">%</span>
      </span>
      <div className="flex gap-1 w-20">
        {config.segments.map((seg, i) => (
          <div key={i} className={`h-1.5 flex-1 rounded-full ${seg}`} />
        ))}
      </div>
      <span
        className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold border ${config.badgeBg} ${config.badgeText} ${config.badgeBorder}`}
      >
        <span className="relative flex h-2 w-2">
          <span
            className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-60 ${config.dotColor}`}
          />
          <span className={`relative inline-flex rounded-full h-2 w-2 ${config.dotColor}`} />
        </span>
        {config.label}
      </span>
    </div>
  );
};

const StatusBadge = ({ status }: { status: string }) => {
  const { t } = useTranslation();

  switch (status) {
    case "COMPLETED":
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
          <CheckCircle size={12} />
          {t("resultPage.status.completed")}
        </span>
      );
    case "FAILED":
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-red-50 text-red-700 border border-red-200">
          <XCircle size={12} />
          {t("resultPage.status.failed")}
        </span>
      );
    case "PROCESSING":
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200">
          <Loader2 size={12} className="animate-spin" />
          {t("resultPage.status.processing")}
        </span>
      );
    case "UPLOADED":
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">
          <Clock size={12} />
          {t("resultPage.status.queued")}
        </span>
      );
    default:
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-600 border border-gray-200">
          <Clock size={12} />
          {status}
        </span>
      );
  }
};

const ResultPage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const applicationIds: string[] =
    (location.state as { application_ids: string[] })?.application_ids || [];

  const [docStatuses, setDocStatuses] = useState<DocStatus[]>(() =>
    applicationIds.map((id) => ({
      id,
      name: id,
      status: "UPLOADED",
      fitness_score: null,
      fakeness_score: null,
    }))
  );

  const jobDescription: string = (location.state as { jobDescription: string })?.jobDescription || "";
  const jobId: string | undefined = (location.state as { jobId?: string })?.jobId;

  const [openFeedback, setOpenFeedback] = useState<string | null>(null);
  const [actions, setActions] = useState<Record<string, string>>({});
  const [showDescModal, setShowDescModal] = useState(false);

  const handleActionChange = (file: string, value: string) => {
    setActions(prev => ({
      ...prev,
      [file]: value,
    }));
  };

  const results = docStatuses;

  const handleSave = async (result: CombinedResult) => {
    const action = actions[result.id];
    if (!action) return;

    try {
      await apiClient.put(
        API_ENDPOINTS.APPLICATION_STATUS(result.id),
        {
          status:
            action === "next"
              ? "Move to Next Round"
              : action === "hold"
                ? "On Hold"
                : "Rejected",
        }
      );

      alert(t("resultPage.alerts.statusUpdated"));
    } catch (err) {
      console.error(err);
      alert(t("resultPage.alerts.statusUpdateFailed"));
    }
  };


  useEffect(() => {
    if (!applicationIds.length || !jobId) return;

    const interval = setInterval(async () => {
      try {
        const response = await apiClient.get(
          API_ENDPOINTS.APPLICATION_DETAILS(jobId)
        );

        const data = response.data as ApplicationResponse[];

        const filteredData = data.filter((app) =>
          applicationIds.includes(String(app.application_id).trim())
        );
        console.log("applicationIds:", applicationIds);
        console.log("API data:", data);

        const newActions: Record<string, string> = {};

        filteredData.forEach((app) => {
          if (app.status) {
            switch (app.status) {
              case "Move to Next Round":
                newActions[app.application_id] = "next";
                break;
              case "On Hold":
                newActions[app.application_id] = "hold";
                break;
              case "Rejected":
                newActions[app.application_id] = "rejected";
                break;
              default:
                break;
            }
          }
        });

        setActions(newActions)

        setDocStatuses(
          filteredData.map((app) => {
            const fullName = app.resume_paths?.split("/").pop() || "";

            const cleanName = fullName.includes("_")
              ? fullName.substring(fullName.indexOf("_") + 1)
              : fullName;

            return {
              id: app.application_id,
              name: cleanName || app.application_id,
              status: (app.processing_status || "UPLOADED")
                .trim()
                .toUpperCase(),
              fitness_score: app.fitness_score,
              fakeness_score: app.fakeness_score,
              feedback: app.feedback,
            };
          })
        );

        const allDone = filteredData.every(
          (a) =>
            a.processing_status?.toUpperCase() === "COMPLETED" ||
            a.processing_status?.toUpperCase() === "FAILED"
        );

        if (allDone) clearInterval(interval);
      } catch (err) {
        console.error(err);
        clearInterval(interval);
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [applicationIds, jobId]);

  const completedCount = docStatuses.filter(d => d.status === "COMPLETED").length;
  const failedCount = docStatuses.filter(d => d.status === "FAILED").length;
  const processingCount = docStatuses.filter(d => d.status === "PROCESSING").length;
  const allDone = docStatuses.length > 0 && docStatuses.every(d => d.status === "COMPLETED" || d.status === "FAILED");

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-[80rem] mx-auto">
        <div className="mb-6">
          <Link
            to={jobId ? `/jobs/${jobId}` : "/dashboard"}
            className="inline-flex items-center text-gray-500 hover:text-primary transition-colors duration-200 group font-medium"
          >
            <div className="p-1.5 rounded-lg bg-white border border-gray-200 group-hover:border-primary/50 group-hover:bg-primary/5 mr-2 transition-all">
              <ArrowLeft className="w-4 h-4" />
            </div>
            {jobId ? t("resultPage.backToJobDetails") : t("resultPage.backToDashboard")}
          </Link>
        </div>

        <div className="bg-white border border-gray-200 p-10 rounded-3xl shadow-xl w-full">
          <h1 className="text-4xl font-extrabold mb-10 text-center text-gray-900">
            {t("resultPage.title")}
          </h1>

          {jobDescription && (
            <div className="mb-8">
              <h2 className="text-2xl font-semibold mb-3 text-gray-900">
                {t("resultPage.jobDescription")}
              </h2>
              <div className="bg-gray-50 border border-gray-200 p-4 rounded-xl shadow-inner">
                <p className="text-gray-700 whitespace-pre-line">
                  {jobDescription.length > 120
                    ? jobDescription.slice(0, 120).trimEnd() + "..."
                    : jobDescription}

                  {jobDescription.length > 120 && (
                    <button
                      onClick={() => setShowDescModal(true)}
                      className="ml-2 text-primary text-sm font-semibold hover:underline"
                    >
                      {t("resultPage.readMore")}
                    </button>
                  )}
                </p>
              </div>
            </div>
          )}

          <div className="mb-10">
            <h2 className="text-2xl font-bold mb-4 text-gray-900">
              {t("resultPage.finalResults")}
            </h2>

            {results.length > 0 ? (
              <div className="space-y-6">
                <div className="grid grid-cols-[2fr_1fr_1fr_1fr_1.2fr] px-6 py-6 bg-gray-100 border border-gray-200 rounded-xl mb-3">
                  <p>{t("resultPage.documentName")}</p>
                  <p className="text-center">{t("resultPage.ras")}</p>
                  <p className="text-center">{t("resultPage.aq")}</p>
                  <p className="text-center">{t("resultPage.feedback")}</p>
                  <p className="text-left pl-6">{t("resultPage.action")}</p>
                </div>

                {results.map((doc, idx) => {
                  const isCompleted = doc.status === "COMPLETED";
                  const fileName = doc.name
                    ? doc.name.split("/").pop() || doc.name
                    : t("resultPage.documentFallback", { number: idx + 1 });

                  return (
                    <div
                      key={doc.id}
                      className="grid grid-cols-[2fr_1fr_1fr_1fr_1.2fr] items-center bg-white p-6 rounded-2xl shadow-sm border border-gray-200 hover:shadow-md transition-all"
                    >
                      <div className="pr-4 min-w-0">
                        <p className="text-sm font-medium text-gray-800 break-words" title={fileName}>
                          {fileName}
                        </p>
                      </div>

                      <div className="flex justify-center items-center">
                        {isCompleted ? (
                          <ScoreIndicator value={Number(doc.fitness_score ?? 0)} max={100} />
                        ) : (
                          <div className="flex justify-center">
                            <Spinner size={SpinnerSize.medium} />
                          </div>
                        )}
                      </div>

                      <div className="flex justify-center items-center">
                        {isCompleted ? (
                          <ScoreIndicator value={Number(doc.fakeness_score ?? 0)} max={100} />
                        ) : (
                          <div className="flex justify-center">
                            <Spinner size={SpinnerSize.medium} />
                          </div>
                        )}
                      </div>

                      <div className="flex justify-center">
                        {isCompleted ? (
                          <button
                            onClick={() => setOpenFeedback(doc.feedback || t("resultPage.noDetailedAnalysis"))}
                            className="text-blue-600 underline text-sm font-semibold hover:text-blue-800"
                          >
                            {t("resultPage.detailedAnalysis")}
                          </button>
                        ) : (
                          <div className="flex justify-center">
                            <Spinner size={SpinnerSize.medium} />
                          </div>
                        )}
                      </div>

                      <div className="flex flex-col gap-2 text-sm pl-6">
                        {isCompleted ? (
                          <>
                            <label className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={actions[doc.id] === "next"}
                                onChange={() => handleActionChange(doc.id, "next")}
                              />
                              {t("resultPage.moveToNextRound")}
                            </label>

                            <label className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={actions[doc.id] === "hold"}
                                onChange={() => handleActionChange(doc.id, "hold")}
                              />
                              {t("resultPage.onHold")}
                            </label>

                            <label className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={actions[doc.id] === "rejected"}
                                onChange={() => handleActionChange(doc.id, "rejected")}
                              />
                              {t("resultPage.rejected")}
                            </label>

                            <button
                              className="mt-1 px-3 py-1 bg-primary text-white rounded-lg text-xs hover:bg-blue-800 transition disabled:opacity-50"
                              disabled={!actions[doc.id]}
                              onClick={() =>
                                handleSave({
                                  id: doc.id,
                                  file: doc.name,
                                  score: Number(doc.fitness_score ?? 0),
                                  authenticityQuotient: Number(doc.fakeness_score ?? 0),
                                  feedback: "",
                                })
                              }
                            >
                              {t("resultPage.save")}
                            </button>
                          </>
                        ) : (
                          <StatusBadge status="UPLOADED" />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              !allDone && (
                <div className="p-6 bg-gray-50 border border-gray-200 text-gray-500 rounded-xl text-center">
                  {t("resultPage.resultsPending")}
                </div>
              )
            )}
          </div>

          <button
            onClick={() => navigate(`/upload/${jobId}`, { state: { jobDescription } })}
            className="w-full py-4 rounded-xl font-semibold shadow-md text-white text-lg transition bg-primary hover:bg-blue-800"
          >
            {t("resultPage.uploadAnother")}
          </button>
        </div>

        {openFeedback && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 animate-fade-in">
            <div className="bg-white w-full max-w-3xl p-8 rounded-3xl shadow-2xl relative animate-scale-in border border-gray-100">
              <button
                onClick={() => setOpenFeedback(null)}
                className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-all"
                aria-label={t("resultPage.closeFeedback")}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 bg-blue-50 rounded-xl">
                  <svg className="w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-gray-900">{t("resultPage.analysisFeedback")}</h2>
              </div>
              <div className="max-h-[60vh] overflow-y-auto pr-2 custom-scrollbar">
                <ul className="text-gray-700 leading-7 text-base space-y-3 list-disc pl-5">
                  {openFeedback
                    ?.split(/(?<=[.!?])\s+/)
                    .filter(Boolean)
                    .map((point, i) => (
                      <li key={i}>{point}</li>
                    ))}
                </ul>
              </div>
              <div className="mt-8 flex justify-end">
                <button
                  onClick={() => setOpenFeedback(null)}
                  className="px-6 py-2.5 bg-primary text-white font-medium rounded-xl hover:bg-blue-800 transition-colors shadow-lg shadow-blue-500/30"
                >
                  {t("resultPage.gotIt")}
                </button>
              </div>
            </div>
          </div>
        )}

        {showDescModal && (
          <div
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowDescModal(false)}
          >
            <div
              className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between mb-4">
                <h3 className="font-semibold">{t("resultPage.jobDescription")}</h3>
                <button onClick={() => setShowDescModal(false)}>&times;</button>
              </div>

              <div className="max-h-80 overflow-y-auto">
                <p className="text-sm whitespace-pre-wrap">
                  {jobDescription}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(300%); }
        }
      `}</style>
    </div>
  );
};

export default ResultPage;
